import { MDXEditor, type MDXEditorMethods } from '@mdxeditor/editor';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import { useColorScheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { useAtom } from 'jotai';
import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import CheckPermissions from '@/components/CheckPermissions';
import FileUploader from '@/components/FileUploader';
import LargeCard from '@/components/LargeCard';
import { PERMISSIONS } from '@/constants/auth.constants';
import {
  getAttachmentUploaderProps,
  isKanatiAttachmentAllowed,
  isKanatiServiceTicket
} from '@/features/TicketCreateDialog/TicketCreateDialog.constants';
import useStaticData from '@/hooks/StaticData.hooks';
import { ticketAtom, ticketIsEditingAtom } from '@/pages/Ticket';
import { mdxFormatDescriptionSummary } from '@/pages/Ticket/Ticket.utils';
import { MDX_EDITOR_CONFIG } from '@/pages/Ticket/TicketCreate/TicketCreate.constants';
import { getSessionStorageData } from '@/utils/Common.utils';

import useStyles from './TicketCreateEventDetails.styles';

const TicketCreateEventDetails = forwardRef<MDXEditorMethods>((_, ref) => {
  const [
    {
      summary,
      ticket_priority,
      title,
      attachments,
      ticket_type,
      ticket_category
    }
  ] = useAtom(ticketAtom);
  const [ticketIsEditing] = useAtom(ticketIsEditingAtom);
  const editorRef = useRef<MDXEditorMethods | null>(null);
  const { mode: themeMode } = useColorScheme();
  const {
    clearErrors,
    control,
    formState: { errors },
    getValues,
    setValue
  } = useFormContext();
  const { priorities } = useStaticData();
  const { classes } = useStyles({ errors });

  const formSummary = useWatch({ control, name: 'summary' });
  const formCategory = useWatch({ control, name: 'ticket_category' });
  // when editing, the form category is not the saved category id; when creating, the atom may hold a previous ticket
  const isKanatiTicket = isKanatiServiceTicket(
    ticket_type,
    ticketIsEditing ? ticket_category?.id : formCategory
  );

  const handleChange = (value: string) => {
    clearErrors('summary');
    setValue('summary', value, { shouldDirty: true });
  };

  const savedDraft = useMemo(() => getSessionStorageData('draft'), []);

  // for summary specifically AND if user reloads the page while editing
  useEffect(() => {
    if (ref != null && typeof ref !== 'function') {
      if (ticketIsEditing) {
        ref?.current?.setMarkdown(
          mdxFormatDescriptionSummary(summary) as string
        );

        setValue('attachments', attachments);
        setValue('summary', summary);
        setValue('ticket_priority', ticket_priority?.id);
        setValue('title', title);
      } else if (savedDraft) {
        ref?.current?.setMarkdown(savedDraft.summary);
      } else {
        ref?.current?.setMarkdown('');
      }
    }
  }, [
    setValue,
    ref,
    summary,
    ticketIsEditing,
    ticket_priority,
    title,
    savedDraft
  ]);

  const setEditorRef = useCallback(
    (instance: MDXEditorMethods | null) => {
      editorRef.current = instance;
      if (typeof ref === 'function') {
        ref(instance);
      } else if (ref) {
        ref.current = instance;
      }
    },
    [ref]
  );

  // whenever form summary changes sync editor UI
  useEffect(() => {
    if (!editorRef.current) return;
    editorRef.current.setMarkdown(formSummary || '');
  }, [formSummary]);

  useEffect(() => {
    if (isKanatiTicket) {
      const files: File[] = getValues('attachments') ?? [];
      // Saved attachments only carry `file_name`, not `name`; only drop newly selected File uploads of a disallowed type
      const filtered = files.filter(
        (file) => !(file instanceof File) || isKanatiAttachmentAllowed(file)
      );
      if (filtered.length !== files.length) {
        setValue('attachments', filtered);
      }
    }
  }, [isKanatiTicket, getValues, setValue]);

  return (
    <LargeCard title="Event Details">
      <Stack direction="column" spacing={2}>
        <div>
          <InputLabel htmlFor="title-input">Title *</InputLabel>
          <FormControl fullWidth>
            <Controller
              control={control}
              name="title"
              rules={{
                required: true
              }}
              render={({ field, formState }) => (
                <TextField
                  id="title-input"
                  error={!!formState.errors.title}
                  {...field}
                />
              )}
            />
          </FormControl>
        </div>
        <CheckPermissions permissions={PERMISSIONS.TICKETS_UPDATE}>
          <div>
            <InputLabel htmlFor="priority-button-group">Priority *</InputLabel>
            <Controller
              control={control}
              name="ticket_priority"
              rules={{
                required: true
              }}
              render={({ field, formState }) => (
                <ButtonGroup
                  id="priority-button-group"
                  color={formState.errors.ticket_priority ? 'error' : 'primary'}
                  variant="outlined"
                >
                  {priorities.map((item) => (
                    <Button
                      key={item.id}
                      variant={
                        field.value === item.id ? 'contained' : 'outlined'
                      }
                      onClick={() => {
                        field.onChange(item.id);
                      }}
                      {...field}
                    >
                      {item.name}
                    </Button>
                  ))}
                </ButtonGroup>
              )}
            />
          </div>
        </CheckPermissions>
        <Stack>
          <InputLabel htmlFor="summary-editor">Summary *</InputLabel>
          <FormControl id="summary-editor" fullWidth>
            <Controller
              control={control}
              name="summary"
              rules={{
                required: true
              }}
              render={() => (
                <div className={classes.summaryErrorBorder}>
                  <MDXEditor
                    className={`${themeMode}-theme dark-editor`}
                    contentEditableClassName="cm-richView"
                    markdown=""
                    plugins={MDX_EDITOR_CONFIG}
                    ref={setEditorRef}
                    suppressHtmlProcessing
                    onChange={(value) => {
                      handleChange(value);
                    }}
                  />
                </div>
              )}
            />
          </FormControl>
        </Stack>
        <FileUploader {...getAttachmentUploaderProps(isKanatiTicket)} />
      </Stack>
    </LargeCard>
  );
});

TicketCreateEventDetails.displayName = 'TicketCreateEventDetails';

export default TicketCreateEventDetails;
